/**
 * Business Claim Types Constants
 * 
 * These constants define the 8 business-relevant claim types supported by the backend.
 * This replaces the previous 3 technical types (DEATH, DISABILITY, CRITICAL_ILLNESS)
 * with specific business claim types that match the backend implementation.
 * 
 * Evidence: Backend defines these types in defaults-seed.js:87-135
 * API: GET /api/defaults/claim-types?businessRelevantOnly=true
 */

export const BUSINESS_CLAIM_TYPES = {
  DTH: { code: 'DTH', displayName: 'Death', category: 'DEATH', order: 1 },
  '010': { code: '010', displayName: 'Spousal Cover', category: 'DEATH', order: 2 },
  '012': { code: '012', displayName: 'Coffin Cover', category: 'DEATH', order: 3 },
  '013': { code: '013', displayName: 'Parent Cover', category: 'DEATH', order: 4 },
  '002': { code: '002', displayName: 'Critical Illness', category: 'CRITICAL_ILLNESS', order: 5 },
  PTD: { code: 'PTD', displayName: 'Permanent Total Disability', category: 'DISABILITY', order: 6 },
  '005': { code: '005', displayName: 'Medical', category: 'MEDICAL', order: 7 },
  TTD: { code: 'TTD', displayName: 'Temporary Total Disability', category: 'DISABILITY', order: 8 }
};

/**
 * Helper function to get business claim type by code
 * @param {string} code - Business claim type code
 * @returns {Object|null} Business claim type object or null if not found
 */
export const getBusinessClaimType = (code) => {
  return BUSINESS_CLAIM_TYPES[code] || null;
};

/**
 * Get all business claim types as an array sorted by order
 * @returns {Array} Array of business claim type objects
 */
export const getBusinessClaimTypesArray = () => {
  return Object.values(BUSINESS_CLAIM_TYPES).sort((a, b) => a.order - b.order);
};

/**
 * Get business claim types by category
 * @param {string} category - Category to filter by (DEATH, DISABILITY, CRITICAL_ILLNESS, MEDICAL)
 * @returns {Array} Array of business claim types in the specified category
 */
export const getBusinessClaimTypesByCategory = (category) => {
  return Object.values(BUSINESS_CLAIM_TYPES)
    .filter(type => type.category === category)
    .sort((a, b) => a.order - b.order);
};

/**
 * Check if a given code is a valid business claim type
 * @param {string} code - Code to validate
 * @returns {boolean} True if code is valid, false otherwise
 */
export const isValidBusinessClaimType = (code) => {
  return code && BUSINESS_CLAIM_TYPES.hasOwnProperty(code);
};